import React, {memo, useDeferredValue, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Bar, Line, Pie} from "react-chartjs-2";
import "../../config/chartConfig";
import {header} from "../../Components/Url/ApiUrl";
import {Logout} from "../../Components/Actions/Actions";

const ACTIVE_STATUS = 3;
const PIE_COLORS = ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#f472b6"];

class AccessDeniedError extends Error {}

let loggingOut = false;

const fetchJson = async (path, signal) => {
    const res = await fetch(process.env.REACT_APP_API_ENDPOINT + path, {headers: header, signal});
    if (res.status === 401) {
        if (!loggingOut) {
            loggingOut = true;
            Logout();
        }
        throw new DOMException("Unauthorized", "AbortError");
    }
    if (res.status === 403) {
        throw new AccessDeniedError("Forbidden");
    }
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }
    return res.json();
};

const regionName = (t, id) => (id ? t(`region${id}`, {defaultValue: "-"}) : "-");

const StatsCharts = memo(function StatsCharts({stats}) {
    const {t} = useTranslation();
    const byRegion = useMemo(() => stats.by_region ?? [], [stats]);
    const byType = stats.by_type ?? [];
    const trend = useMemo(() => stats.trend ?? [], [stats]);
    const summary = stats.summary ?? {};
    const scoreSummary = stats.score_summary ?? {};

    const regionLabels = useMemo(() => byRegion.map((r) => regionName(t, r.region)), [byRegion, t]);
    const barData = useMemo(() => ({
        labels: regionLabels,
        datasets: [{
            label: t("Registerga kirgan"),
            data: byRegion.map((r) => r.high_score),
            backgroundColor: "rgba(56,189,248,0.7)",
            borderColor: "#38bdf8",
            borderWidth: 1,
        }],
    }), [byRegion, regionLabels, t]);
    const pieData = useMemo(() => ({
        labels: regionLabels,
        datasets: [{
            label: t("Yuqori ball olgan korxonalar"),
            data: byRegion.map((r) => r.high_score),
            backgroundColor: byRegion.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        }],
    }), [byRegion, regionLabels, t]);
    const trendData = useMemo(() => ({
        labels: trend.map((r) => r.month),
        datasets: [{
            label: t("Yangi kompaniyalar"),
            data: trend.map((r) => r.count),
            borderColor: "#22c55e",
            backgroundColor: "rgba(34,197,94,0.2)",
            tension: 0.3,
            fill: true,
        }],
    }), [trend, t]);

    const regionTotal = byRegion.reduce(
        (acc, r) => ({
            total: acc.total + Number(r.total),
            scored: acc.scored + Number(r.scored),
            not_scored: acc.not_scored + Number(r.not_scored),
            high_score: acc.high_score + Number(r.high_score),
        }),
        {total: 0, scored: 0, not_scored: 0, high_score: 0}
    );

    return (
        <>
            <div className="row g-3 mb-4">
                <div className="col-md-3 col-sm-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body text-center">
                            <h2 className="h6 text-muted">{t("total_companies")}</h2>
                            <p className="h3 fw-bold text-primary mb-0">{summary.total}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body text-center text-success">
                            <h2 className="h6 text-muted">{t("Faol")}</h2>
                            <p className="h3 fw-bold mb-0">{summary.active}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body text-center text-warning">
                            <h2 className="h6 text-muted">{t("Kutilayotgan")}</h2>
                            <p className="h3 fw-bold mb-0">{summary.pending}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body text-center">
                            <h2 className="h6 text-muted">{t("Baholangan kompaniyalar")}</h2>
                            <p className="h3 fw-bold text-info mb-0">{scoreSummary.scored}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3 mb-4">
                <div className="col-lg-4">
                    <div className="card shadow-sm h-100">
                        <h2 className="card-header h6 fw-semibold mb-0">{t("Viloyatlar kesimida")}</h2>
                        <div className="card-body table-responsive">
                            <table className="table table-striped table-sm text-center">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">{t("Viloyat")}</th>
                                    <th scope="col">{t("Jami")}</th>
                                    <th scope="col">{t("Ball berilgan")}</th>
                                    <th scope="col">{t("no_score")}</th>
                                    <th scope="col">{t("60+ ball")}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {byRegion.map((r, i) => (
                                    <tr key={r.region ?? `none-${i}`}>
                                        <td className="text-start">{regionName(t, r.region)}</td>
                                        <td>{r.total}</td>
                                        <td className="text-success">{r.scored}</td>
                                        <td className="text-danger">{r.not_scored}</td>
                                        <td className="text-primary fw-semibold">{r.high_score}</td>
                                    </tr>
                                ))}
                                <tr className="fw-bold table-secondary">
                                    <td>{t("Jami")}</td>
                                    <td>{regionTotal.total}</td>
                                    <td className="text-success">{regionTotal.scored}</td>
                                    <td className="text-danger">{regionTotal.not_scored}</td>
                                    <td className="text-primary">{regionTotal.high_score}</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card shadow-sm mb-3">
                        <h2 className="card-header h6 fw-semibold mb-0">{t("Registerga kirgan")}</h2>
                        <div className="card-body">
                            <Bar data={barData} role="img" aria-label={t("Registerga kirgan")}/>
                        </div>
                    </div>
                    <div className="card shadow-sm">
                        <h2 className="card-header h6 fw-semibold mb-0">{t("Oylik qo‘shilish trendi")}</h2>
                        <div className="card-body">
                            <Line data={trendData} role="img" aria-label={t("Oylik qo‘shilish trendi")}/>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card shadow-sm mb-3">
                        <h2 className="card-header h6 fw-semibold mb-0">{t("Yuqori ball olgan korxonalar")}</h2>
                        <div className="card-body">
                            <Pie data={pieData} role="img" aria-label={t("Yuqori ball olgan korxonalar")}/>
                        </div>
                    </div>
                    <div className="card shadow-sm">
                        <h2 className="card-header h6 fw-semibold mb-0">{t("Kompaniya turlari")}</h2>
                        <div className="card-body">
                            <table className="table table-hover text-center align-middle mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">{t("Turi")}</th>
                                    <th scope="col">{t("Soni")}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {byType.map((c, i) => (
                                    <tr key={c.type ?? `none-${i}`}>
                                        <td>{c.type ?? "-"}</td>
                                        <td>{c.count}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

function CompanyTable({list}) {
    const {t} = useTranslation();
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);

    const filtered = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        if (!q) return list;
        return list.filter((c) => (c.name || "").toLowerCase().includes(q) || String(c.stir ?? "").includes(q));
    }, [list, deferredSearch]);

    return (
        <div className="card mb-4 shadow-sm">
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h2 className="h6 fw-semibold mb-0">{t("companies_list")} ({filtered.length})</h2>
                <input
                    type="search"
                    className="form-control form-control-sm w-auto"
                    placeholder={t("search_by_name")}
                    aria-label={t("search_by_name")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className="card-body table-responsive">
                <table className="table table-bordered table-hover text-center align-middle mb-0">
                    <thead className="table-light">
                    <tr>
                        <th scope="col">#</th>
                        <th scope="col">{t("Nomi")}</th>
                        <th scope="col">{t("stir")}</th>
                        <th scope="col">{t("Viloyat")}</th>
                        <th scope="col">{t("Status")}</th>
                        <th scope="col">{t("score")}</th>
                        <th scope="col">{t("Sana")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((c, i) => (
                        <tr key={c.id}>
                            <td>{i + 1}</td>
                            <td className="text-start">{c.name ?? "-"}</td>
                            <td>{c.stir ?? "-"}</td>
                            <td>{regionName(t, c.region)}</td>
                            <td>
                                {Number(c.status) === ACTIVE_STATUS
                                    ? <span className="badge bg-success">{t("Faol")}</span>
                                    : <span className="badge bg-warning text-dark">{t("Kutilayotgan")}</span>}
                            </td>
                            <td>{c.total_point == null || c.total_point === "" ? "-" : c.total_point}</td>
                            <td>{c.created_at ? String(c.created_at).slice(0, 10) : "-"}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const {t} = useTranslation();
    const [stats, setStats] = useState(null);
    const [list, setList] = useState([]);
    const [status, setStatus] = useState("loading"); // loading | ready | empty | denied | error
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setStatus("loading");
        Promise.all([
            fetchJson("/app/prokuratura/dashboard", controller.signal),
            fetchJson("/app/prokuratura/companies", controller.signal),
        ])
            .then(([dashboard, companies]) => {
                const loadedStats = dashboard?.companies ?? null;
                setStats(loadedStats);
                setList(Array.isArray(companies) ? companies : []);
                setStatus(loadedStats ? "ready" : "empty");
            })
            .catch((err) => {
                if (err?.name === "AbortError") return;
                console.error(err);
                setStatus(err instanceof AccessDeniedError ? "denied" : "error");
            });
        return () => controller.abort();
    }, [attempt]);

    if (status === "loading") {
        return <div className="text-center py-5 text-muted" role="status">{t("loading")}</div>;
    }
    if (status === "denied") {
        return (
            <div className="text-center py-5" role="alert">
                <p className="text-danger">{t("Sizda bu bo'limga kirish huquqi yo'q.", {defaultValue: "Sizda bu bo'limga kirish huquqi yo'q."})}</p>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={Logout}>{t("exit")}</button>
            </div>
        );
    }
    if (status === "error" || status === "empty") {
        return (
            <div className="text-center py-5" role="alert">
                <p className="text-danger">{t("error_load_dashboard")}</p>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setAttempt((n) => n + 1)}>
                    <i className="bi bi-arrow-clockwise pe-2" aria-hidden="true"></i>{t("retry", {defaultValue: "Qayta urinish"})}
                </button>
            </div>
        );
    }

    return (
        <div className="container-fluid my-4">
            <StatsCharts stats={stats}/>
            <CompanyTable list={list}/>
        </div>
    );
}
